export interface UploadResult {
  previewImages: string[];
  destinationProductLinks: string[];
  destinationProductLinkText: string;
}

export interface DesignFile {
  file: File;
  position: 'Front' | 'Back' | 'Left' | 'Right';
}

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DropboxService {
  private accessToken: string = '';
  private apiBase = "https://api.dropboxapi.com/2";
  private contentBase = "https://content.dropboxapi.com/2";
  private serverBase = "/api/Dropbox";

  constructor(private http: HttpClient) {
    this.initializeToken();
  }

  getCurrentAccessToken(): string {
    return this.accessToken;
  }

  private generateUniqueId(): string {
    // Generate a unique ID using timestamp and random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${randomStr}`;
  }

  private async initializeToken(): Promise<void> {
    try {
      const result = await this.checkAccessToken();
      if (result.isValid) {
        this.accessToken = result.accessToken;
      }
    } catch (error) {
      console.error("Failed to initialize token:", error);
    }
  }

  async checkAccessToken(): Promise<{isValid: boolean, accessToken: string, wasRefreshed: boolean}> {
    try {
      const result = await this.http.post<{isValid: boolean, accessToken: string, wasRefreshed: boolean}>(`${this.serverBase}/CheckAccessToken`, {}).toPromise();
      return result || { isValid: false, accessToken: '', wasRefreshed: false };
    } catch (err) {
      console.error("Error checking token:", err);
      return { isValid: false, accessToken: '', wasRefreshed: false };
    }
  }

  async refreshAccessToken(): Promise<string> {
    console.log("Attempting to refresh access token...");
    
    try {
      const data = await this.http.post<{accessToken: string}>(`${this.serverBase}/RefreshToken`, {}).toPromise();
      
      if (data && data.accessToken) {
        this.accessToken = data.accessToken;
        console.log("Access token refreshed successfully:", this.accessToken.substring(0, 20) + "...");
        return this.accessToken;
      } else {
        throw new Error("No access token received from server");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  }

  async uploadFile(dropboxPath: string, fileContent: Blob | string): Promise<any> {
    // Ensure we have a valid token before uploading
    if (!this.accessToken) {
      const tokenResult = await this.checkAccessToken();
      if (!tokenResult.isValid) {
        throw new Error("No valid access token available");
      }
      this.accessToken = tokenResult.accessToken;
    }

    try {
      const result = await this.http.post<any>(`${this.contentBase}/files/upload`, fileContent, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          "Dropbox-API-Arg": JSON.stringify({
            path: dropboxPath,
            mode: "add",
            autorename: false,
            mute: false,
          }),
          "Content-Type": "application/octet-stream",
        }
      }).toPromise();
      
      return result;
    } catch (error) {
      if (error.status === 401) {
        // Token expired, refresh and retry
        await this.refreshAccessToken();
        return this.uploadFile(dropboxPath, fileContent);
      }
      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  async createSharedLink(path: string): Promise<string> {
    try {
      // Ensure we have a valid token
      if (!this.accessToken) {
        const tokenResult = await this.checkAccessToken();
        if (!tokenResult.isValid) {
          throw new Error("No valid access token available");
        }
        this.accessToken = tokenResult.accessToken;
      }

      const data = await this.http.post<any>(`${this.apiBase}/sharing/create_shared_link_with_settings`, {
        path: path,
        settings: {
          requested_visibility: "public",
          audience: "public",
          access: "viewer"
        }
      }, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          "Content-Type": "application/json",
        }
      }).toPromise();

      return data.url.replace("dl=0", "raw=1");
    } catch (error) {
      if (error.status === 401) {
        console.warn("Access token expired, refreshing...");
        await this.refreshAccessToken();
        return this.createSharedLink(path);
      }
      
      // Fallback to get existing shared link
      return this.getSharedLink(path);
    }
  }

  async getSharedLink(path: string): Promise<string> {
    // Ensure we have a valid token
    if (!this.accessToken) {
      const tokenResult = await this.checkAccessToken();
      if (!tokenResult.isValid) {
        throw new Error("No valid access token available");
      }
      this.accessToken = tokenResult.accessToken;
    }

    try {
      const data = await this.http.post<any>(`${this.apiBase}/sharing/get_shared_link_file`, {}, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          "Dropbox-API-Arg": JSON.stringify({
            url: path
          }),
        }
      }).toPromise();

      return data.url.replace("dl=0", "raw=1");
    } catch (error) {
      if (error.status === 401) {
        console.warn("Access token expired, refreshing...");
        await this.refreshAccessToken();
        return this.getSharedLink(path);
      }
      
      throw new Error(`Get shared link failed: ${error.message}`);
    }
  }

  async uploadDesignFiles(designFiles: DesignFile[]): Promise<UploadResult> {
    const result: UploadResult = {
      previewImages: [],
      destinationProductLinks: [],
      destinationProductLinkText: ""
    };

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    }).replace(/\//g, ' ');
    const timeStr = now.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    }).replace(/:/g, '');

    // Initialize with 4 empty rows (Front, Back, Left, Right)
    const positionLinks = ['', '', '', ''];
    const positionOrder = { 'Front': 0, 'Back': 1, 'Left': 2, 'Right': 3 };

    // Create upload promises for parallel processing
    const uploadPromises = designFiles.map(async (designFile, index) => {
      const file = designFile.file;
      const position = designFile.position;
      
      try {
        // Generate unique ID for this file
        const uniqueId = this.generateUniqueId();
        const fileExtension = file.name.split('.').pop();
        const baseFileName = file.name.replace(/\.[^/.]+$/, "");
        
        // Create thumbnail (resize to 500x500)
        const thumbnailBlob = await this.createThumbnail(file, 500, 500);
        
        // Upload thumbnail with unique ID
        // const thumbRemotePath = `/UploadDesignHere/${dateStr}/${timeStr}_t${uniqueId}_${baseFileName}.${fileExtension}`;
        // const thumbUploadResult = await this.uploadFile(thumbRemotePath, thumbnailBlob);
        // const thumbLink = await this.createSharedLink(thumbUploadResult.path_lower);
        
        // Upload original file with unique ID
        const remotePath = `/UploadDesignHere/${dateStr} ${timeStr}/${uniqueId}_${baseFileName}.${fileExtension}`;
        const uploadResult = await this.uploadFile(remotePath, file);
        const desLink = await this.createSharedLink(uploadResult.path_lower);
        
        // Return result with position info
        return {
          desLink,
          position,
          positionIndex: positionOrder[position],
          index
        };
        
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        throw error;
      }
    });

    // Wait for all uploads to complete in parallel
    const uploadResults = await Promise.all(uploadPromises);
    
    // Process results and populate arrays
    uploadResults.forEach((uploadResult) => {
      result.previewImages.push(uploadResult.desLink);
      result.destinationProductLinks.push(uploadResult.desLink);
      
      // Add to position-specific array based on position
      positionLinks[uploadResult.positionIndex] = uploadResult.desLink;
    });

    // Create the structured link text with 4 rows (Front, Back, Left, Right)
    result.destinationProductLinkText = positionLinks.join("\n");
    return result;
  }

  private async createThumbnail(file: File, width: number, height: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = width;
        canvas.height = height;
        
        // Calculate scaling to maintain aspect ratio
        const scale = Math.min(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image
        const x = (width - scaledWidth) / 2;
        const y = (height - scaledHeight) / 2;
        
        ctx?.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create thumbnail'));
          }
        }, 'image/jpeg', 0.9);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

}
